import { useFlag } from '@reflag/react-sdk';
import { skipToken, useQuery } from '@tanstack/react-query';

import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../../navigation';
import { apiQuery, useCurrentOrganization } from '../../../state';
import type { OrganizationAgentLocation } from '../../Agent';
import { getSiteGitSyncSetupTarget } from '../../GitSync';
import type { SiteSetupChoice, SiteSetupStepBehavior } from './types';

/**
 * The Git Sync step: its choice opens the site's Git Sync setup instead of asking the agent, when
 * the user can set it up there.
 *
 * @param props.siteId - The setup conversation's site, if it is one.
 * @param props.location - Where the conversation is shown, which decides where the setup opens.
 */
export function useGitSyncStep(props: {
    siteId: string | undefined;
    location: OrganizationAgentLocation;
}): SiteSetupStepBehavior {
    const { siteId, location } = props;
    const organization = useCurrentOrganization();
    const { navigate } = useNavigation();
    const { data: site, isPending: isSitePending } = useQuery(
        apiQuery('getSiteById', siteId ? { organizationId: organization.id, siteId } : skipToken)
    );
    const gitSyncFlag = useFlag('NATIVE_GIT_SYNC_SITES');
    // The setup dialog closes right away without the flag or for non-admins: the agent guides them.
    const canSetUpGitSync = gitSyncFlag.isEnabled && Boolean(site?.permissions.admin);

    const onChoice = useEventCallback((choice: SiteSetupChoice) => {
        if (choice.opens !== 'git-sync' || !siteId || !canSetUpGitSync) {
            return false;
        }
        // In place over the agent screen. From the dock, the current screen may be a space, whose
        // Git Sync is the space's own, or one with no side sheet, so it goes to the site instead.
        navigate(
            location.surface === 'agent_screen'
                ? { screen: { sideSheet: { type: 'gitsync', tab: 'setup' } } }
                : getSiteGitSyncSetupTarget({ id: siteId }, organization.id)
        );
        return true;
    });

    return { onChoice, isLoading: gitSyncFlag.isLoading || (Boolean(siteId) && isSitePending) };
}
